import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import { getStepsForRole, getTourById } from '../config/onboardingSteps';

const STORAGE_KEY = 'msk_onboarding_completed';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userRole, setUserRole] = useState('all');
  const [tourSteps, setTourSteps] = useState(null); // null = use default onboarding steps

  const defaultSteps = useMemo(() => getStepsForRole(userRole), [userRole]);
  const steps = tourSteps || defaultSteps;
  const currentStep = steps[currentStepIndex] || null;
  const totalSteps = steps.length;

  const isCompleted = useCallback(() => {
    return localStorage.getItem(STORAGE_KEY) === '1';
  }, []);

  const startOnboarding = useCallback((role = 'all') => {
    setTourSteps(null); // reset to default onboarding steps
    setUserRole(role);
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const startTour = useCallback((tourId) => {
    const steps = getTourById(tourId);
    if (steps.length === 0) return;
    setTourSteps(steps);
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      setIsActive(false);
      setTourSteps(null);
      localStorage.setItem(STORAGE_KEY, '1');
    }
  }, [currentStepIndex, totalSteps]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  const skipOnboarding = useCallback(() => {
    setIsActive(false);
    setTourSteps(null);
    localStorage.setItem(STORAGE_KEY, '1');
  }, []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCurrentStepIndex(0);
  }, []);

  const value = useMemo(() => ({
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    steps,
    startOnboarding,
    startTour,
    nextStep,
    prevStep,
    skipOnboarding,
    resetOnboarding,
    isCompleted,
  }), [
    isActive, currentStep, currentStepIndex, totalSteps, steps,
    startOnboarding, startTour, nextStep, prevStep, skipOnboarding, resetOnboarding, isCompleted,
  ]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
